import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  Truck,
  Radio,
  Smartphone,
  Shield,
  UtensilsCrossed,
  Users,
  Wrench,
  Boxes,
} from "lucide-react";
import { api } from "@/lib/api";

const ICONS = {
  truck: Truck,
  radio: Radio,
  smartphone: Smartphone,
  shield: Shield,
  utensils: UtensilsCrossed,
  users: Users,
  wrench: Wrench,
  boxes: Boxes,
};

export default function Apps() {
  const [apps, setApps] = useState([]);

  useEffect(() => {
    api.get("/apps").then((r) => setApps(r.data)).catch(() => setApps([]));
  }, []);

  return (
    <main className="bg-[var(--bg)] text-[var(--text)] min-h-screen" data-testid="apps-page">
      <header className="border-b border-[var(--border)] sticky top-0 bg-[var(--bg)]/90 backdrop-blur-md z-10">
        <div className="container-nk py-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-muted hover:text-gold font-mono text-[11px] uppercase tracking-[0.18em] transition-colors"
            data-testid="apps-back-home"
          >
            <ArrowLeft size={14} />
            Back to Naami
          </Link>
          <span className="inline-flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.18em] text-muted">
            <Shield size={12} />
            Internal · Staff Use
          </span>
        </div>
      </header>

      <section className="container-nk pt-20 pb-12 lg:pt-28 lg:pb-16">
        <p className="eyebrow text-gold">// Naami Apps</p>
        <h1
          className="font-display mt-4 leading-[0.95]"
          style={{ fontSize: "clamp(56px, 9vw, 110px)" }}
          data-testid="apps-headline"
        >
          The <span className="text-gold">Naami</span><br />
          Toolkit.
        </h1>
        <p className="mt-6 max-w-2xl text-[15.5px] leading-relaxed text-[var(--text)]/80 font-light">
          Web-based companion apps for the Naami team. Open these on any
          phone — no install needed. Tap "Add to Home Screen" to keep them
          one-tap away.
        </p>
      </section>

      <section className="container-nk pb-16">
        {apps.length === 0 ? (
          <p className="text-muted text-[13px]">No apps yet — add some from the admin panel.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {apps.map((app) => {
              const Icon = ICONS[app.icon] || Truck;
              return (
                <a
                  key={app.id}
                  href={app.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="apps-card group"
                  data-testid={`apps-card-${app.id}`}
                  style={{ "--accent": app.color || "#C8A97E" }}
                >
                  <div className="apps-card-icon">
                    <Icon size={28} />
                  </div>
                  <div className="apps-card-body">
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--accent)] mb-2">
                      {app.audience}
                    </p>
                    <h2 className="font-display text-[34px] leading-tight">{app.name}</h2>
                    <p className="text-[14px] text-[var(--text)]/75 mt-3 leading-relaxed">
                      {app.description}
                    </p>
                    <span className="inline-flex items-center gap-2 mt-6 font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent)] transition-all group-hover:gap-3">
                      Open {app.name}
                      <ArrowUpRight size={14} />
                    </span>
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </section>

      <section className="container-nk pb-24">
        <div className="border border-[var(--border)] rounded-xl bg-[var(--surface)] p-8 md:p-12 relative overflow-hidden">
          <div
            className="absolute -top-20 -right-20 w-[280px] h-[280px] rounded-full bg-[var(--gold)] opacity-[0.06] blur-3xl pointer-events-none"
            aria-hidden
          />
          <div className="relative z-10 grid md:grid-cols-[auto_1fr] gap-8 md:gap-12 items-start">
            <div className="w-16 h-16 rounded-2xl bg-[var(--gold)] flex items-center justify-center flex-shrink-0">
              <Smartphone size={28} className="text-[var(--bg)]" />
            </div>
            <div>
              <p className="eyebrow text-gold">// Quick Tip</p>
              <h3 className="font-display text-[32px] md:text-[40px] leading-none mt-3">
                Add to your home screen
              </h3>
              <p className="mt-4 text-muted text-[14px] leading-relaxed max-w-2xl">
                These apps run in your browser — no App Store install needed.
                Add them to your phone's home screen so they open like native
                apps with zero clutter.
              </p>

              <div className="grid sm:grid-cols-2 gap-5 mt-7">
                <div className="border border-[var(--border)] rounded-lg p-5 bg-[var(--surface-elevated)]">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold mb-2.5">
                    iPhone · Safari
                  </p>
                  <ol className="text-[13px] text-[var(--text)]/85 leading-relaxed space-y-1 list-decimal list-inside">
                    <li>Open the app link above</li>
                    <li>Tap the Share icon (square with arrow)</li>
                    <li>Choose "Add to Home Screen"</li>
                    <li>Tap "Add"</li>
                  </ol>
                </div>
                <div className="border border-[var(--border)] rounded-lg p-5 bg-[var(--surface-elevated)]">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold mb-2.5">
                    Android · Chrome
                  </p>
                  <ol className="text-[13px] text-[var(--text)]/85 leading-relaxed space-y-1 list-decimal list-inside">
                    <li>Open the app link above</li>
                    <li>Tap the ⋮ menu (top-right)</li>
                    <li>Choose "Add to Home screen"</li>
                    <li>Tap "Install"</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[var(--border)] bg-[var(--surface)]">
        <div className="container-nk py-6 flex justify-between items-center text-[11px] font-mono uppercase tracking-[0.16em] text-muted">
          <span>© {new Date().getFullYear()} Naami Kitchens · Internal</span>
          <Link to="/" className="hover:text-gold transition-colors" data-testid="apps-footer-home">
            naamikitchens.com
          </Link>
        </div>
      </footer>
    </main>
  );
}
